(() => {
  // app/scripts/components/CardProduct.js
  var { moneyFormat } = theme.settings;
  var {
    getRegionData,
    countDecimals,
    calculateRegionInventoryTotal,
    getPickupQuantity,
    addQuantityAndUnitPrice,
    getEcoFee
  } = theme.utils;
  var CardProduct = class extends HTMLElement {
    _price = null;
    _source = null;
    _b2b_key = null;
    constructor() {
      super();
      this.form = this.querySelector("form");
      this.quantity = this.querySelector("quantity-input");
      this.JSONData = JSON.parse(this.querySelector("script[data-json]")?.textContent || "{}");
      this.unitPriceLabel = this.querySelector(".js-unit-price");
      this.selectRegionButton = this.querySelector(".js-select-region");
      this.pricingData = this.JSONData.pricing;
      this.priceLabel = this.querySelector(".js-price-value");
      this.ecoFeeLabels = this.querySelectorAll(".js-eco-fee");
      this.ecoFeeData = this.JSONData.ecoFee;
      this.currentEcoFee = 0;
      this.erpNumber = this.dataset.erpNumber;
      this.oversize = this.dataset.oversize;
      this.PCProducts = this.JSONData.PCProducts;
      this.myPartInput = this.querySelector("add-my-part");
      if (theme.customer.isB2B) {
        const selectedRegion = getRegionData();
        const foundEcoFee = getEcoFee(this.ecoFeeData, selectedRegion);
        this.updateEcoFee(foundEcoFee);
        this.updateLocationName(theme.preferredLocation?.name);
      }
      this.initButtonEvents();
      this.handleSubmit = this.handleSubmit.bind(this);
      this.updateLayoutByRegion = this.updateLayoutByRegion.bind(this);
      this.handlePreferredLocationChange = this.handlePreferredLocationChange.bind(this);
    }
    set price(price) {
      if (isNaN(price)) {
        console.error("Price is not a number");
        return;
      }
      this._price = Number(price);
      this.updatePrice(this._price);
    }
    get price() {
      return this._price + this.currentEcoFee;
    }
    get source() {
      return this._source;
    }
    set source(source) {
      this._source = source;
    }
    get b2b_key() {
      return this._b2b_key;
    }
    set b2b_key(key) {
      this._b2b_key = key;
    }
    getCurrentQuantity() {
      const quantity = Number.parseInt(this.quantity?.value, 10);
      return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
    }
    async connectedCallback() {
      await customElements.whenDefined("quantity-input");
      this.initEvents();
      if (theme.customer.isB2B) {
        window.addEventListener("preferredLocation:change", this.handlePreferredLocationChange);
        this.quantity?.onChange(this.handleQuantityChange.bind(this));
      }
    }
    disconnectedCallback() {
      this.form?.removeEventListener("submit", this.handleSubmit);
      window.removeEventListener("regionSelected", this.updateLayoutByRegion);
      window.removeEventListener("preferredLocation:change", this.handlePreferredLocationChange);
    }
    initButtonEvents() {
      this.selectRegionButton?.addEventListener(
        "click",
        (e) => {
          e.stopPropagation();
          window.dispatchEvent(new CustomEvent("open-select-region"));
        },
        {}
      );
    }
    get properties() {
      const properties = { _source: this._source };
      if (theme.customer.isB2B) {
        properties._b2b_key = this._b2b_key;
        properties._b2b_price = String(this._price);
        this.myPartInput?.value && (properties._my_part_number = this.myPartInput.value);
      }
      if (this.currentEcoFee) {
        properties._ecofee = this.currentEcoFee;
      }
      if (countDecimals(this._price) > 2) {
        properties._quantity = this.quantity.value;
      }
      return properties;
    }
    async handleSubmit(event) {
      try {
        event.preventDefault();
        this.setState("adding");
        const formData = new FormData(this.form);
        const quantity = +formData.get("quantity");
        const id = this.form.id.value;
        formData.append("sections", "drawer-cart-items,header-cart-popup");
        formData.append("sections_url", `${theme.routes.searchUrl}?q=[variants.id:${id}]`);
        formData.append("properties[_source]", this._source);
        if (theme.customer.isB2B) {
          this.myPartInput?.value ? formData.append("properties[_my_part_number]", this.myPartInput.value) : formData.delete("properties[_my_part_number]");
          formData.append("properties[_b2b_price]", String(this._price));
          formData.append("properties[_b2b_key]", this._b2b_key);
        }
        if (this.currentEcoFee) {
          formData.append("properties[_ecofee]", this.currentEcoFee);
        }
        if (countDecimals(this._price) > 2) {
          addQuantityAndUnitPrice(formData, this._price, quantity);
        }
        const dataToChange = await Cart.checkItemExists({
          id,
          quantity,
          erpNumber: this.erpNumber,
          price: this._price
        });
        if (dataToChange) {
          await Cart.change({
            data: {
              ...dataToChange,
              sections: formData.get("sections"),
              sections_url: formData.get("sections_url")
            },
            action: "add"
          });
        } else {
          await Cart.add(formData);
        }
        if (window.theme.routes.pageType == "cart") {
          window.location.reload();
        }
      } catch (error) {
        throw error;
      } finally {
        this.setState("reset");
      }
    }
    async initEvents() {
      this.form?.addEventListener("submit", this.handleSubmit);
      if (theme.customer.isB2B)
        return;
      const regionSelector = document.querySelector("select-region");
      if (regionSelector) {
        await customElements.whenDefined("select-region");
        window.addEventListener("regionSelected", this.updateLayoutByRegion);
        window.addEventListener("regionDetected", this.updateLayoutByRegion);
      }
      const selectedRegion = getRegionData();
      selectedRegion && this.updateLayoutByRegion({ detail: { regionData: selectedRegion } });
      this.setState("fetched-price");
    }
    handlePreferredLocationChange(event) {
      const { preferredLocation } = event.detail;
      const priceInventory = window.PriceInventory.storage.find(this.erpNumber);
      this.updateLocationName(preferredLocation.name);
      priceInventory && this.updateB2BLayout(priceInventory?.data, preferredLocation);
      const foundRegion = theme.regionData.find((region) => region.title == preferredLocation.region);
      const foundEcoFee = getEcoFee(this.ecoFeeData, foundRegion);
      this.updateEcoFee(foundEcoFee);
    }
    updateLayoutByRegion({ detail }) {
      const selectedRegion = detail.regionData;
      const { provinces, customer_id, location_id } = selectedRegion;
      let foundEcoFee = this.ecoFeeData.find(
        (ecoFee) => provinces.some((province) => province.toLowerCase() === ecoFee.Province.toLowerCase())
      );
      const foundShopLocation = theme.shopLocations.find((location) => location.id == location_id);
      const foundPCProduct = this.findPCProductAndUpdateLayout(foundShopLocation?.pc_number);
      const available = this.oversize === "false" && !!foundPCProduct?.InStock;
      const foundPricing = this.pricingData.find((pricing) => pricing.CustomerID == customer_id);
      this.setState(available ? "stock-available" : "stock-unavailable");
      this.updateEcoFee(foundEcoFee?.Fee);
      if (foundPricing) {
        const sorted = [...foundPricing.Pricing || []].sort((a, b) => a.QtyBreak - b.QtyBreak);
        const { Pricing = 0, Source } = sorted[0] || {};
        const price = Number(Pricing) || 0;
        if (price) {
          this.price = price;
          this._source = Source;
          this.setState("price-available");
        } else {
          this.setState("price-unavailable");
        }
      } else {
        this.setState("price-unavailable");
      }
    }
    updateB2BLayout(priceInventory, shopLocation) {
      const { Quantity, WarehousesQuantities = [] } = priceInventory || {};
      const { b2bDeliveryQuantity } = theme.settings;
      const pickupQuantity = getPickupQuantity(WarehousesQuantities, shopLocation, Quantity);
      const totalInventoryQuantity = b2bDeliveryQuantity === "region" ? calculateRegionInventoryTotal(WarehousesQuantities, shopLocation, Quantity) : pickupQuantity;
      const { Pricing, Source, key } = PriceInventory.findPricingByQtyBreak(this.erpNumber, this.quantity?.value || 1);
      this.findPCProductAndUpdateLayout(shopLocation.pc_number);
      this.setState("fetched-price");
      this.price = +Pricing;
      this.source = Source;
      this.b2b_key = key;
      if (+Pricing === 0) {
        this.setState("price-unavailable");
        return;
      }
      if (totalInventoryQuantity) {
        this.updateTotalInventory(totalInventoryQuantity);
        if (pickupQuantity > 0) {
          this.updatePickupInventory(pickupQuantity);
          this.setState("pickup-available");
        } else {
          this.setState("pickup-unavailable");
        }
      } else {
        this.setState("stock-unavailable");
      }
    }
    findPCProductAndUpdateLayout(pcNumber) {
      if (!pcNumber)
        return;
      const foundPCProduct = this.PCProducts.find((pcProduct) => pcProduct.WarehouseName == pcNumber);
      this.updateQuantityStep(foundPCProduct?.MultipleSaleQty);
      this.updateMinQuantity(foundPCProduct?.MinimumOrderQty);
      return foundPCProduct;
    }
    updateEcoFee(ecoFee) {
      this.currentEcoFee = Number(ecoFee) || 0;
      if (!this.ecoFeeLabels.length)
        return;
      const ecoFeeMoney = theme.settings.moneyFormat.replace("{{amount}}", this.currentEcoFee.toFixed(2));
      this.ecoFeeLabels.forEach((label) => {
        if (this.currentEcoFee) {
          label.textContent = label.dataset.message.replace("{{money}}", ecoFeeMoney);
          label.classList.remove("hidden");
        } else {
          label.classList.add("hidden");
        }
      });
    }
    updatePrice(price) {
      if (!this.priceLabel)
        return;
      const totalPrice = Math.round((price + this.currentEcoFee) * 100) / 100;
      this.priceLabel.textContent = moneyFormat.replace("{{amount}}", totalPrice.toFixed(2));
      this.handleAfterPriceUpdate();
    }
    updateUnitPrice(newUnitPrice) {
      if (!this.unitPriceLabel)
        return;
      if (newUnitPrice) {
        this.unitPriceLabel.textContent = `/ ${newUnitPrice}`;
        this.unitPriceLabel.classList.remove("hidden");
      } else {
        this.unitPriceLabel.classList.add("hidden");
      }
      this.handleAfterPriceUpdate();
    }
    updateTotalInventory(quantity) {
      this.querySelectorAll(".js-total-inventory").forEach((elm) => {
        elm.textContent = quantity;
      });
    }
    updatePickupInventory(quantity) {
      this.querySelectorAll(".js-pickup-inventory").forEach((elm) => {
        elm.textContent = quantity;
      });
    }
    updateQuantityStep(step) {
      if (!this.quantity || !step)
        return;
      this.quantity.updateStep(step);
      if (step > 1) {
        const messageElm = this.querySelector(".js-sold-multiples-message");
        if (messageElm) {
          messageElm.textContent = messageElm.dataset.message.replace("{{ sold_multiples }}", step);
        }
        this.setState("sold-multiples");
      } else {
        this.setState("sold-single");
      }
    }
    updateMinQuantity(number) {
      if (!this.quantity || !number)
        return;
      this.quantity.input.min = number;
    }
    updateLocationName(name) {
      const locationNameElm = this.querySelector(".js-location-name");
      if (!locationNameElm)
        return;
      const locationId = window.gic_customer.WarehouseName;
      const locationName = name || theme.shopLocations.find((location) => location.pc_number == locationId)?.name;
      locationNameElm.textContent = locationName;
    }
    handleQuantityChange(newQuantity) {
      const pricing = PriceInventory.findPricingByQtyBreak(this.erpNumber, newQuantity);
      if (!pricing)
        return;
      if (this._price != pricing.Pricing) {
        this.price = pricing.Pricing;
      }
    }
    setState(state) {
      switch (state) {
        case "stock-unavailable":
          this.classList.add("stock-unavailable");
          break;
        case "stock-available":
          this.classList.remove("stock-unavailable");
          break;
        case "pickup-unavailable":
          this.classList.add("pickup-unavailable");
          break;
        case "pickup-available":
          this.classList.remove("pickup-unavailable");
          break;
        case "sold-multiples":
          this.classList.add("sold-multiples");
          break;
        case "sold-single":
          this.classList.remove("sold-multiples");
          break;
        case "fetched-price":
          this.classList.remove("is-fetching-price");
          break;
        case "price-not-found":
          this.classList.remove("is-fetching-price");
          this.classList.add("price-missing");
          break;
        case "price-unavailable":
          this.classList.add("price-unavailable");
          break;
        case "price-available":
          this.classList.remove("price-unavailable");
          break;
        case "deleting":
          this.classList.add("is-loading", "is-deleting");
          break;
        case "adding":
          this.classList.add("is-loading", "is-atc-loading");
          break;
        case "reset":
          this.classList.remove("is-loading", "is-atc-loading", "is-deleting");
      }
    }
    // Override this method in child class
    handleAfterPriceUpdate() {
    }
  };

  // app/scripts/components/CardProductExtended.js
  var { moneyFormat: moneyFormat2 } = theme.settings;
  var CardProductExtended = class extends CardProduct {
    constructor() {
      super();
      this.isAvailable = null;
      this.totalPriceElm = this.querySelector(".js-total-price");
      this.checkbox = this.querySelector("input[type='checkbox']");
    }
    async connectedCallback() {
      await super.connectedCallback();
      this.initDeleteButton();
      this.updateTotalPrice();
      this.quantity.onChange(this.updateTotalPrice.bind(this));
      this.checkbox.addEventListener("change", this.handleCheckboxChange.bind(this));
    }
    updateTotalPrice() {
      this.totalPriceElm.textContent = moneyFormat2.replace("{{amount}}", (this.price * this.quantity.value).toFixed(2));
      this.dispatch("quantity-change");
    }
    handleCheckboxChange(event) {
      this.dispatch("card-item-select");
    }
    // This method is called after the price is updated from CardProduct
    handleAfterPriceUpdate() {
      this.updateTotalPrice();
    }
    initDeleteButton() {
      this.deleteButtons = this.querySelectorAll(".js-delete-btn");
      this.deleteButtons.forEach((button) => {
        button.addEventListener("click", (e) => this.dispatch("delete-item"));
      });
    }
    dispatch(type) {
      switch (type) {
        case "card-item-select":
          this.checkbox.dispatchEvent(
            new CustomEvent("card-item-select", {
              detail: { variantId: this.dataset.variantId },
              bubbles: true
            })
          );
          break;
        case "quantity-change":
          this.dispatchEvent(
            new CustomEvent("card-item-quantity-change", {
              detail: { variantId: this.dataset.variantId, quantity: this.quantity.value },
              bubbles: true
            })
          );
          break;
        case "delete-item":
          this.dispatchEvent(
            new CustomEvent("card-item-delete", { detail: { variantId: this.dataset.variantId }, bubbles: true })
          );
          break;
      }
    }
    setState(state) {
      super.setState(state);
      this.isAvailable = !["stock-unavailable", "price-missing"].some((c) => this.classList.contains(c));
      if (!this.classList.contains("is-oversized")) {
        this.isAvailable = true;
      }
      if (this.classList.contains("is-oversized") && theme.customer.isB2B && !this.classList.contains("price-missing")) {
        this.isAvailable = true;
      }
      switch (state) {
        case "price-not-found":
          super.setState("price-unavailable");
          super.setState("stock-unavailable");
          break;
      }
    }
  };
  customElements.define("card-product-extended", CardProductExtended);

  // app/scripts/components/DownloadTemplate.js
  var DownloadTemplate = class extends HTMLElement {
    constructor() {
      super();
      this.addEventListener("click", this.handleDownloadTemplate.bind(this));
    }
    handleDownloadTemplate() {
      if (!XLSX) {
        throw new Error("XLSX is not loaded");
      }
      const data = [
        ["Manufacturer #", "Quantity (mandatory)"],
        ["example: xyz", "1"],
        ["example: ABC-DE123890", "1"]
      ];
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet(data);
      XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
      const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([wbout], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "import-example.xlsx";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };
  customElements.define("download-template", DownloadTemplate);

  // app/scripts/quick-order.js
  var { checkAndUpdateQuantity } = theme.utils;
  var QuickOrderComponent = class extends HTMLElement {
    constructor() {
      super();
      const $ = (selector) => this.querySelector(selector);
      this.firstConnected = false;
      this.isHydratingProducts = false;
      this.selectedProducts = /* @__PURE__ */ new Set();
      this.productLists = /* @__PURE__ */ new Map();
      this.addToListBtn = $(".js-atjl-btn");
      this.productImporter = $("product-importer");
      this.productSelector = $("product-selector");
      this.quickOrderForm = $("#quick-order-form");
      this.selectedCountLabel = $(".js-selected-count");
      this.productListContainer = $(".js-product-list");
      this.selectAll = $("#quick-order-heading-checkbox");
      this.deleteSelectedBtn = $(".js-delete-selected");
      this.deleteConfirmation = $("popup-delete-confirm");
    }
    async connectedCallback() {
      if (this.firstConnected)
        return;
      this.firstConnected = true;
      await customElements.whenDefined("product-selector");
      await customElements.whenDefined("product-importer");
      this.productImporter.onUpload(this.handleProductAddedToSelection.bind(this));
      this.productSelector.onAdd(this.handleProductAddedToSelection.bind(this));
      this.selectAll.addEventListener("change", this.handleSelectAllChange.bind(this));
      this.quickOrderForm.addEventListener("submit", this.handleAddToCart.bind(this));
      this.addToListBtn?.addEventListener("click", this.handleAddToList.bind(this));
      this.deleteSelectedBtn.addEventListener("click", this.handleDeleteSelected.bind(this));
      this.addEventListener("card-item-select", this.updateCheckedCount.bind(this));
      this.addEventListener("card-item-delete", this.handleDeleteItem.bind(this));
      this.addEventListener("card-item-quantity-change", this.handleCardItemQuantityChange.bind(this));
      this.loadFromStorage();
      window.PriceInventory.fetchLivePriceInventory();
    }
    async handleDeleteSelected() {
      try {
        const selectedCards = this.getSelectedProductCards();
        if (selectedCards.length === 0) {
          return;
        }
        const confirmed = await this.deleteConfirmation.confirm();
        if (!confirmed) {
          return;
        }
        this.setState("loading");
        selectedCards.forEach((card) => {
          const variantId = card.dataset.variantId;
          this.productLists.delete(variantId);
          card.remove();
          this._updateSelectedStatus(variantId, false);
        });
        this.saveToStorage();
        this.updateCount();
        this.setImporterAlreadyExists();
        this.deleteConfirmation.close(true);
      } catch (error) {
        throw error;
      } finally {
        this.setState("reset");
      }
    }
    async handleAddToCart(event) {
      try {
        this.setState("adding-to-cart");
        event.preventDefault();
        const productCards = this.getSelectedProductCards().filter((card) => card.isAvailable);
        const variantIDs = productCards.map((card) => card.dataset.variantId);
        const itemsToAdd = [];
        let lastItemToChange = null;
        for (const card of productCards) {
          card.quantity.validateAndDispatch();
          const dataToChange = await Cart.checkItemExists({
            id: card.dataset.variantId,
            quantity: card.quantity.value,
            erpNumber: card.erpNumber,
            price: card._price
          });
          if (dataToChange) {
            if (!lastItemToChange) {
              lastItemToChange = dataToChange;
              continue;
            }
            await Cart.change({
              data: dataToChange
            });
          } else {
            itemsToAdd.push(
              checkAndUpdateQuantity(
                {
                  id: card.dataset.variantId,
                  properties: card.properties
                },
                card._price,
                card.quantity.value
              )
            );
          }
        }
        const sections = Cart.addDefaultSections(variantIDs);
        if (itemsToAdd.length > 0) {
          const data = { items: itemsToAdd };
          if (!lastItemToChange) {
            Object.assign(data, sections);
          }
          await Cart.add(data);
        }
        if (lastItemToChange) {
          await Cart.change({
            data: { ...lastItemToChange, ...sections },
            action: "add"
          });
        }
        productCards.forEach((card) => {
          const variantId = card.dataset.variantId;
          this.productLists.delete(variantId);
          card.remove();
          this._updateSelectedStatus(variantId, false);
        });
        this.saveToStorage();
        this.updateCount();
        this.setImporterAlreadyExists();
      } catch (error) {
        throw error;
      } finally {
        this.setState("reset");
      }
    }
    async handleAddToList() {
      try {
        const itemsToAdd = this.getSelectedProductCards().map((card) => {
          card.quantity.validateAndDispatch();
          const product = this.productLists.get(card.dataset.variantId);
          return {
            handle: product.clickUri.split("/").pop(),
            variantGid: product.ec_product_id,
            productGid: `gid://shopify/Product/${product.ec_item_group_id}`,
            quantity: parseInt(card.quantity.value) || 1
          };
        });
        const response = await JobLists.modal.selectJobList.select(itemsToAdd, "add");
        if (response === false)
          return;
      } catch (error) {
        throw error;
      }
    }
    getSelectedProductCards() {
      return [...this.quickOrderForm.elements].filter((element) => element.tagName === "INPUT" && element.checked).map((input) => input.closest("card-product-extended")).filter(Boolean);
    }
    async renderProducts() {
      this.isHydratingProducts = true;
      try {
        const arr = [...this.productLists.values()];
        const cardQuickOrderList = [];
        do {
          const variantIds = arr.splice(0, 50).map(({ ec_product_id }) => ec_product_id.split("/").pop());
          const arrPromises = variantIds.map(
            (variantId) => fetch(`${Shopify.routes.root}variants/${variantId}?section_id=card-quick-order`).then((res) => res.text()).then(convertStringToCardElement.bind(this)).catch((error) => {
              console.warn("error", error);
              return null;
            })
          );
          const newCards = await Promise.all(arrPromises);
          cardQuickOrderList.push(...newCards);
        } while (arr.length > 0);
        this.productListContainer.innerHTML = "";
        this.productListContainer.append(...cardQuickOrderList);
        this.updateCount();
        await customElements.whenDefined("card-product-extended");
        const cards = [...this.querySelectorAll("card-product-extended")];
        this.selectedProducts.clear();
        cards.forEach((card) => {
          const product = this.productLists.get(card.dataset.variantId);
          if (product.checked) {
            card.checkbox.checked = true;
            this.selectedProducts.add(card.dataset.variantId);
          }
          if (product.quantityStepUp) {
            card.quantity.stepUp(product.quantityStepUp);
            return;
          }
          if (product.quantity > 1) {
            card.quantity.stepUp(Math.ceil((product.quantity - card.quantity.value) / card.quantity.step));
          }
        });
        this.dataset.checkedCount = this.selectedProducts.size;
        window.PriceInventory.fetchLivePriceInventory();
      } catch (error) {
        throw error;
      } finally {
        this.isHydratingProducts = false;
      }
    }
    updateCheckedCount(event) {
      this._updateSelectedStatus(event.detail.variantId, event.target.checked);
      this.saveToStorage();
    }
    _updateSelectedStatus(variantId, checked) {
      checked ? this.selectedProducts.add(variantId) : this.selectedProducts.delete(variantId);
      this.dataset.checkedCount = this.selectedProducts.size;
      const product = this.productLists.get(variantId);
      if (product)
        product.checked = checked;
    }
    handleCardItemQuantityChange(event) {
      if (this.isHydratingProducts)
        return;
      const { variantId } = event.detail;
      const card = event.target.closest("card-product-extended");
      const product = this.productLists.get(variantId);
      if (!card || !product)
        return;
      const syncedProduct = {
        ...product,
        quantity: card.getCurrentQuantity()
      };
      if (Number(syncedProduct?.quantity) === Number(product.quantity))
        return;
      this.productLists.set(variantId, syncedProduct);
      this.saveToStorage();
    }
    async handleDeleteItem(event) {
      const { variantId } = event.detail;
      this.productLists.delete(variantId);
      this.saveToStorage();
      this.querySelector(`card-product-extended[data-variant-id="${variantId}"]`)?.remove();
      this.updateCount();
      this._updateSelectedStatus(variantId, false);
      this.setImporterAlreadyExists();
    }
    updateCount() {
      this.selectedCountLabel.textContent = itemString(this.productLists.size);
      this.dataset.count = this.productLists.size;
    }
    // This method is called when the product is added to the selection from the product selector or the product importer
    handleProductAddedToSelection(selectedItems) {
      this.mergeSelectedProducts(selectedItems, { autoCheckNew: true });
      this.renderProducts();
      this.saveToStorage();
      this.setImporterAlreadyExists();
    }
    handleSelectAllChange(event) {
      const inputs = [...this.quickOrderForm.elements].filter((element) => element.tagName === "INPUT");
      const { checked } = event.target;
      inputs.forEach((input) => {
        const parent = input.closest("card-product-extended");
        if (parent) {
          input.checked = checked;
          if (input.name === "product-selection") {
            this._updateSelectedStatus(parent.dataset.variantId, checked);
          }
        }
      });
      this.saveToStorage();
    }
    mergeSelectedProducts(products, { autoCheckNew = false } = {}) {
      products.forEach((product) => {
        const id = product.ec_product_id.split("/").pop();
        if (!this.productLists.has(id)) {
          if (autoCheckNew)
            product.checked = true;
          this.productLists.set(id, product);
        }
      });
    }
    setState(state) {
      switch (state) {
        case "loading":
          this.classList.add("is-loading");
          break;
        case "adding-to-cart":
          this.classList.add("is-adding-to-cart", "is-loading");
          break;
        case "reset":
          this.classList.remove("is-adding-to-cart", "is-loading");
          break;
      }
    }
    saveToStorage() {
      const items = [...this.productLists.values()];
      localStorage.setItem("quick-order-items", JSON.stringify(items));
    }
    loadFromStorage() {
      const items = JSON.parse(localStorage.getItem("quick-order-items"));
      if (items) {
        this.mergeSelectedProducts(items);
        this.renderProducts();
        this.setImporterAlreadyExists();
      }
    }
    setImporterAlreadyExists() {
      this.productImporter.setAlreadyExists([...this.productLists.values()]);
    }
  };
  function convertStringToCardElement(string) {
    const div = document.createElement("div");
    div.innerHTML = string.replace("[form_id]", this.quickOrderForm.id);
    return div.querySelector(`card-product-extended`);
  }
  function itemString(count) {
    switch (count) {
      case 0:
        return theme.string.items.zero;
      case 1:
        return theme.string.items.one.replace("{{count}}", count);
      default:
        return theme.string.items.other.replace("{{count}}", count);
    }
  }
  customElements.define("quick-order-component", QuickOrderComponent);
})();
